import React, { useEffect, useRef, useCallback } from 'react';

import moment, { Moment } from 'moment';
import styled from 'styled-components';

import { KEYS } from '../../../../../../../../constants/Common';

import Masks from '../../../../../../../../services/Masks';

import connectAuth, { IAuthMapStateToProps, IConnectedProps } from '../../../../hooks/connectAuth';

import Input from '../../../../../Common/Form/Input/Input';
import InputTrailingIcon from '../../../../../Common/Form/Input/InputTrailingIcon';
import { multipleActions } from '../../../../Store/ContainerReducer';
import { IAuthState } from '../../../../Store/Reducers';
import { IKeyDown, Types } from '../../../../Store/SignUpReducer';
import DatePicker from './DatePicker';


const FIELD_ID = 'birth-date';

interface IBirthDateSelector {
    reset: boolean,
    error: string,
    minDate: Moment,
    maxDate: Moment,
    touched: boolean,
    keyDown: IKeyDown,
    dateFormat: string,
    date: Moment | null,
    displayDate: string,
    useUsFormat: boolean,
    openDatePicker: boolean,
    customPlaceholder?: string
}

type TBirthDateSelector = IConnectedProps<IBirthDateSelector>;

const Container = styled.div`
    width: 100%;
    display: flex;
    position: relative;
    align-items: center;
    flex-direction: column;
`;

export const validateDateValue = (
    displayDate: string,
    minDate: Moment,
    maxDate: Moment,
    useUsFormat: boolean,
    intl: any
): { error: string; date: Moment | null } => {
    if (displayDate.length === 0) {
        return { error: intl.formatMessage({ id: 'authFunctional.emptyField' }), date: null };
    }

    if (displayDate.length < 10) {
        return { error: intl.formatMessage({ id: 'authFunctional.inputDate.invalid' }), date: null };
    }

    const newDate = moment(Masks.commonToISO8601(displayDate, useUsFormat));

    if (newDate.isBefore(maxDate)) {
        return { error: intl.formatMessage({ id: 'authFunctional.inputDate.overMaxAge' }), date: null };
    }

    if (newDate > minDate) {
        return { error: intl.formatMessage({ id: 'authFunctional.inputDate.underAge' }), date: null };
    }

    return { error: '', date: newDate };
};

const BirthDateSelector: React.FC<TBirthDateSelector> = ({ intl, dispatch, ...props }) => {
    const refInput = useRef<HTMLInputElement>(null);

    const {
        date,
        error,
        keyDown,
        minDate,
        maxDate,
        touched,
        dateFormat,
        useUsFormat,
        displayDate,
        openDatePicker,
        customPlaceholder
    } = props;

    const placeholder = customPlaceholder || intl.formatMessage({ id: 'authFunctional.inputDate.placeholder' });

    const trailingTabIndex = (displayDate.length === 10 && !error) ? -1 : 0;

    const setError = useCallback((newError: string) => dispatch({
        type: Types.CHANGE_DATE_DATA,
        data: { error: newError }
    }), [dispatch]);

    const updateDateValue = useCallback((value: string) => {

        if (value.length > 10) return;

        const newStringDate = Masks.date(value, useUsFormat);

        dispatch({
            type: Types.CHANGE_DATE_DATA,
            data: {
                touched: true,
                displayDate: newStringDate,
                error: ''
            }
        });
    }, [dispatch, useUsFormat]);

    const handleClickPicker = useCallback(() => {
        const validation = validateDateValue(displayDate, minDate, maxDate, useUsFormat, intl);

        multipleActions(dispatch, [
            {
                type: Types.OPEN_DATE_PICKER,
                data: { datePickerDate: validation.date }
            },
            {
                type: Types.CHANGE_DATE_DATA,
                data: { touched: true }
            },
            {
                type: Types.CHANGE_HOUR_DATA,
                data: { openTimePicker: false }
            }
        ]);
    }, [dispatch, validateDateValue]);

    const handleBlurDate = () => {
        const result = validateDateValue(displayDate, minDate, maxDate, useUsFormat, intl);

        dispatch({
            type: Types.CHANGE_DATE_DATA,
            data: {
                date: result.date,
                error: result.error
            }
        });
    };

    const handleChangeDate = ({ target }: React.ChangeEvent<HTMLInputElement>) => updateDateValue(target.value);

    const handleOnPickerDateChanged = (newDate: moment.Moment) => {
        const isNewDate = !displayDate || (!date && newDate) || (date !== null && newDate.format('L') !== date.format('L'));

        if (!isNewDate) return;

        dispatch({
            type: Types.CHANGE_DATE_DATA,
            data: {
                date: newDate,
                displayDate: newDate.format(dateFormat),
                error: ''
            }
        });
    };

    useEffect(() => {
        if (!keyDown || keyDown && !keyDown.key) return;

        if (keyDown.key !== KEYS.ENTER || document.activeElement?.id !== `input-${FIELD_ID}`) return;

        handleClickPicker();
    }, [keyDown, dispatch, handleClickPicker]);

    return (
        <Container>
            <Input
                order={2}
                id={FIELD_ID}
                error={error}
                ref={refInput}
                pattern="[0-9]*"
                inputMode="numeric"
                value={displayDate}
                onBlur={handleBlurDate}
                disabled={openDatePicker}
                placeholder={placeholder}
                onChange={handleChangeDate}
            >
                <InputTrailingIcon
                    width={15}
                    height={17}
                    tabIndex={trailingTabIndex}
                    onClick={handleClickPicker}
                >
                    <g fill="none" fillRule="evenodd" transform="translate(.2 .2)">
                        <path d="M1.6 0H3.2V1.6H1.6zM11.2 0H12.799999999999999V1.6H11.2z" />
                        <rect width="14.4" height="14.4" y="1.6" rx=".8" />
                        <path fill="#FFF" d="M1.6 5.6H12.799999999999999V14.4H1.6z" />
                        <path d="M7.2 8.8H11.2V12.8H7.2z" />
                    </g>
                </InputTrailingIcon>
            </Input>

            <DatePicker onDateChanged={handleOnPickerDateChanged} />
        </Container>
    );
};

const mapStateToProps: IAuthMapStateToProps<IBirthDateSelector> = (state, isComparison = false) => ((!isComparison)
    ? {
        reset: (state as IAuthState).Container.reset,
        date: (state as IAuthState).SignUp.birthData.birthDate.date,
        dateFormat: (state as IAuthState).Container.i18n.dateFormat,
        useUsFormat: (state as IAuthState).Container.i18n.useUSFormat,
        error: (state as IAuthState).SignUp.birthData.birthDate.error,
        minDate: (state as IAuthState).SignUp.birthData.birthDate.minDate,
        maxDate: (state as IAuthState).SignUp.birthData.birthDate.maxDate,
        touched: (state as IAuthState).SignUp.birthData.birthDate.touched,
        displayDate: (state as IAuthState).SignUp.birthData.birthDate.displayDate,
        openDatePicker: (state as IAuthState).SignUp.birthData.birthDate.openDatePicker,
        customPlaceholder: (state as IAuthState).Container.communication.placeholderBirthDate,
        keyDown: ((state as IAuthState).SignUp.keyDown.key === KEYS.ENTER) ? (state as IAuthState).SignUp.keyDown : ''
    } :
    {
        date: state.date,
        reset: state.reset,
        error: state.error,
        keyDown: state.keyDown,
        minDate: state.minDate,
        maxDate: state.maxDate,
        touched: state.touched,
        dateFormat: state.dateFormat,
        useUsFormat: state.useUsFormat,
        displayDate: state.displayDate,
        openDatePicker: state.openDatePicker,
        customPlaceholder: state.customPlaceholder
    }
);

export default connectAuth(mapStateToProps)(BirthDateSelector);
